import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de inventario y movimientos de stock
 * Basado en la API documentada en Betel_Natural_Inventario_Module
 */
const inventarioService = {
  // ==================== GESTIÓN DE STOCK ====================

  /**
   * Obtener inventario actual de productos
   * @param {Object} params - Parámetros de filtrado
   * @param {boolean} params.include_inactive - Incluir productos inactivos (default: false)
   * @param {boolean} params.low_stock_only - Solo productos con stock bajo (default: false)
   * @param {boolean} params.out_of_stock_only - Solo productos agotados (default: false)
   * @param {number} params.skip - Offset para paginación
   * @param {number} params.limit - Límite de resultados
   */
  getInventory: async (params = {}) => {
    try {
      const response = await apiClient.get('/inventory', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar stock de un producto
   * @param {number} productId - ID del producto
   * @param {Object} stockData - Datos de la actualización
   * @param {string} stockData.operation - Tipo: 'add', 'subtract', 'set'
   * @param {number} stockData.quantity - Cantidad
   * @param {string} stockData.reason - Razón del movimiento
   * @param {string} stockData.notes - Notas adicionales
   * @param {number} stockData.unit_cost - Costo unitario (opcional)
   */
  updateStock: async (productId, stockData) => {
    try {
      console.log('Actualizando stock:', productId, stockData);
      const response = await apiClient.put(`/products/${productId}/stock`, stockData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateStock:', error);
      return handleApiError(error);
    }
  },

  /**
   * Actualización masiva de stock
   */
  bulkStockUpdate: async (updates) => {
    try {
      const response = await apiClient.post('/products/bulk-stock-update', updates);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== MOVIMIENTOS DE INVENTARIO ====================

  /**
   * Obtener movimientos de inventario
   * @param {Object} params - Parámetros de filtrado
   * @param {number} params.skip - Offset
   * @param {number} params.limit - Límite
   * @param {number} params.product_id - Filtrar por producto
   */
  getMovements: async (params = {}) => {
    try {
      const response = await apiClient.get('/products/inventory/movements', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener movimientos por producto
   */
  getMovementsByProduct: async (productId, params = {}) => {
    try {
      const response = await apiClient.get('/products/inventory/movements', {
        params: { ...params, product_id: productId }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== ALERTAS DE STOCK ====================

  /**
   * Obtener productos con stock bajo
   */
  getLowStockProducts: async () => {
    try {
      const response = await apiClient.get('/products/inventory/low-stock');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener alertas de stock con nivel de urgencia
   */
  getStockAlerts: async () => {
    try {
      const response = await apiClient.get('/products/inventory/alerts');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener alertas de stock bajo para órdenes de compra
   */
  getLowStockAlertsForPurchase: async (params = {}) => {
    try {
      const response = await apiClient.get('/purchase-orders/low-stock-alerts', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener productos con stock bajo agrupados por proveedor
   */
  getLowStockBySupplier: async (params = {}) => {
    try {
      const response = await apiClient.get('/purchase-orders/minimum-stock/by-supplier', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== CONFIGURACIÓN DE STOCK ====================

  /**
   * Actualizar configuración de stock de un producto
   */
  updateStockConfig: async (productId, config) => {
    try {
      const response = await apiClient.put(`/purchase-orders/products/${productId}/stock-config`, null, {
        params: config
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualización masiva de mínimos de stock
   */
  bulkUpdateMinimums: async (updates) => {
    try {
      const response = await apiClient.post('/purchase-orders/products/bulk-update-minimums', updates);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== AUTOMATIZACIÓN ====================

  /**
   * Generar órdenes de compra automáticamente basado en stock bajo
   */
  autoGeneratePurchaseOrders: async (params = {}) => {
    try {
      const response = await apiClient.post('/purchase-orders/auto-generate', null, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Generar órdenes masivas por proveedor
   */
  bulkOrderBySupplier: async (supplierIds, department = 'betel') => {
    try {
      const params = {
        supplier_ids: supplierIds,
        department
      };
      const response = await apiClient.post('/purchase-orders/bulk-order-by-supplier', null, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== ESTADÍSTICAS Y REPORTES ====================

  /**
   * Obtener estadísticas de inventario
   */
  getStats: async () => {
    try {
      const response = await apiClient.get('/products/inventory/stats');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener reporte de compras
   */
  getPurchasesReport: async (params = {}) => {
    try {
      const response = await apiClient.get('/purchase-orders/reports/purchases', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener reporte de desempeño de proveedores
   */
  getSupplierPerformanceReport: async (params = {}) => {
    try {
      const response = await apiClient.get('/purchase-orders/reports/supplier-performance', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== UTILIDADES ====================

  /**
   * Tipos de operaciones de stock
   */
  getStockOperations: () => {
    return [
      { value: 'add', label: 'Agregar Stock', icon: 'plus' },
      { value: 'subtract', label: 'Restar Stock', icon: 'minus' },
      { value: 'set', label: 'Ajustar Stock', icon: 'edit' }
    ];
  },

  /**
   * Razones comunes para movimientos de stock
   */
  getStockReasons: () => {
    return {
      add: [
        'Recepción de compra',
        'Devolución de cliente',
        'Ajuste de inventario',
        'Producción interna',
        'Transferencia entre almacenes'
      ],
      subtract: [
        'Venta',
        'Devolución a proveedor',
        'Merma',
        'Producto dañado',
        'Uso interno',
        'Transferencia entre almacenes'
      ],
      set: [
        'Ajuste por inventario físico',
        'Corrección de error',
        'Sincronización de sistema'
      ]
    };
  },

  /**
   * Validar datos de actualización de stock
   */
  validateStockUpdate: (stockData) => {
    const errors = {};

    if (!stockData.operation) {
      errors.operation = 'El tipo de operación es requerido';
    }

    if (!stockData.quantity || stockData.quantity <= 0) {
      errors.quantity = 'La cantidad debe ser mayor a 0';
    }

    if (!stockData.reason) {
      errors.reason = 'La razón del movimiento es requerida';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

export default inventarioService;
